import type { AudioEngine } from '../types/audio.js';

interface StateCallbacks {
  onBeatUpdate: (beat: number) => void;
  onDebugUpdate: (info: DebugInfo) => void;
  onLevelUpdate: (levels: LevelData[]) => void;
}

interface DebugInfo {
  currentSample: number;
  contextTime: number;
  loopStatus: string;
  playbackMode: string;
  audioState: string;
}

interface LevelData {
  peak: number;
  trackIndex: number;
}

interface BeatCounterConfig {
  beatsPerTrack: number;
  currentTempo: number;
  sampleRate: number;
  musicalLoopLengthSamples: number;
}

/**
 * AppStateManager - Manages application state updates and animations
 */
export class AppStateManager {
  private callbacks: StateCallbacks;
  private beatAnimationFrame?: number;
  private meterAnimationFrame?: number;
  private lastBeat = -1;

  // Cached state for animations
  private currentEngine?: AudioEngine;
  private isPlaying = false;
  private beatConfig?: BeatCounterConfig;
  private audioContext?: AudioContext;
  private analyserNodes: AnalyserNode[] = [];
  private meterData: Uint8Array[] = [];

  constructor(callbacks: StateCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Start beat counter animation
   */
  startBeatCounter(): void {
    if (this.beatAnimationFrame) {
      return;
    } // Already running

    const updateBeat = (): void => {
      if (this.isPlaying && this.currentEngine && this.beatConfig) {
        const currentBeat = Math.floor(
          this.currentEngine.currentCount % this.beatConfig.beatsPerTrack
        );

        if (currentBeat !== this.lastBeat) {
          // Display as 1-based instead of 0-based
          this.callbacks.onBeatUpdate(currentBeat + 1);
          this.lastBeat = currentBeat;
        }

        // Update debug counters
        this.updateDebugCounters();
      }

      this.beatAnimationFrame = requestAnimationFrame(updateBeat);
    };

    this.beatAnimationFrame = requestAnimationFrame(updateBeat);
  }

  /**
   * Stop beat counter animation
   */
  stopBeatCounter(): void {
    if (this.beatAnimationFrame) {
      cancelAnimationFrame(this.beatAnimationFrame);
      this.beatAnimationFrame = undefined;
    }
  }

  /**
   * Update beat counter state
   */
  updateBeatCounter(
    engine: AudioEngine,
    isPlaying: boolean,
    config: BeatCounterConfig,
    audioContext?: AudioContext
  ): void {
    this.currentEngine = engine;
    this.isPlaying = isPlaying;
    this.beatConfig = config;
    this.audioContext = audioContext;
  }

  /**
   * Update debug counters
   */
  private updateDebugCounters(): void {
    if (!this.currentEngine || !this.beatConfig) {
      return;
    }

    // Calculate current sample position
    const beatsPerSecond = this.beatConfig.currentTempo / 60;
    const samplesPerBeat = this.beatConfig.sampleRate / beatsPerSecond;
    const rawSample = Math.floor(this.currentEngine.currentCount * samplesPerBeat);

    // Ensure positive value by adding loop length before modulo
    const currentSample =
      ((rawSample % this.beatConfig.musicalLoopLengthSamples) +
        this.beatConfig.musicalLoopLengthSamples) %
      this.beatConfig.musicalLoopLengthSamples;

    // Determine loop status
    let loopStatus = 'Playing';
    if (currentSample > this.beatConfig.musicalLoopLengthSamples - 1000) {
      loopStatus = 'Near End - Loop Coming';
    } else if (currentSample < 1000) {
      loopStatus = 'Loop Start';

      // Log when we detect a loop restart
      if (currentSample < 100) {
        console.log(
          '🔄 Loop detected at sample:',
          currentSample,
          'Engine state:',
          this.currentEngine.state
        );
      }
    }

    // Determine playback mode
    const playbackMode = this.beatConfig.currentTempo === 120 ? 'Tempo-Synced' : 'Basic';

    this.callbacks.onDebugUpdate({
      currentSample,
      contextTime: this.audioContext?.currentTime || 0,
      loopStatus,
      playbackMode,
      audioState: this.currentEngine.state || 'Unknown'
    });
  }

  /**
   * Start level meter animation
   */
  startLevelMeters(): void {
    if (this.meterAnimationFrame) {
      return;
    } // Already running

    const updateMeters = (): void => {
      if (this.isPlaying && this.analyserNodes.length > 0) {
        const levels: LevelData[] = [];

        // Update each track's level meter
        this.analyserNodes.forEach((analyser, index) => {
          if (analyser && this.meterData[index]) {
            // Get frequency data
            analyser.getByteFrequencyData(this.meterData[index]);

            const data = this.meterData[index];

            // Find peak value
            let peak = 0;
            for (let i = 0; i < data.length; i++) {
              if (data[i] > peak) {
                peak = data[i];
              }
            }

            const peakLevel = (peak / 255) * 100;
            levels.push({ peak: peakLevel, trackIndex: index });
          }
        });

        if (levels.length > 0) {
          this.callbacks.onLevelUpdate(levels);
        }
      }

      this.meterAnimationFrame = requestAnimationFrame(updateMeters);
    };

    this.meterAnimationFrame = requestAnimationFrame(updateMeters);
  }

  /**
   * Stop level meter animation
   */
  stopLevelMeters(): void {
    if (this.meterAnimationFrame) {
      cancelAnimationFrame(this.meterAnimationFrame);
      this.meterAnimationFrame = undefined;
    }
  }

  /**
   * Update level meter state
   */
  updateLevelMeters(
    analyserNodes: AnalyserNode[],
    meterData: Uint8Array[],
    isPlaying: boolean
  ): void {
    this.analyserNodes = analyserNodes;
    this.meterData = meterData;
    this.isPlaying = isPlaying;
  }
}
